/**
 * Route Persistence Service
 * Saves and restores the current route when the app is backgrounded/foregrounded
 */

class RoutePersistenceService {
  private static readonly ROUTE_KEY = 'fitness_app_current_route';
  private static readonly ROUTE_STATE_KEY = 'fitness_app_route_state';
  private static readonly MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Save current route.
   * Don't persist state for /chat so that a refresh always shows the conversation list.
   */
  static saveRoute(pathname: string, state?: any): void {
    try {
      const shouldPersistState = state && pathname !== '/chat';
      const routeData = {
        pathname,
        state: shouldPersistState ? state : undefined,
        timestamp: Date.now(),
      };

      sessionStorage.setItem(this.ROUTE_KEY, JSON.stringify(routeData));

      if (shouldPersistState) {
        sessionStorage.setItem(this.ROUTE_STATE_KEY, JSON.stringify(state));
      } else if (pathname === '/chat') {
        sessionStorage.removeItem(this.ROUTE_STATE_KEY);
      }
    } catch (error) {
      console.error('Error saving route:', error);
    }
  }

  /**
   * Get saved route
   */
  static getSavedRoute(): { pathname: string; state?: any } | null {
    try {
      const saved = sessionStorage.getItem(this.ROUTE_KEY);
      
      if (!saved) {
        return null;
      }

      const routeData = JSON.parse(saved);
      const now = Date.now();

      // Check if route is too old
      if (now - routeData.timestamp > this.MAX_AGE) {
        this.clearRoute();
        return null;
      }

      let state = null;
      if (routeData.state) {
        const savedState = sessionStorage.getItem(this.ROUTE_STATE_KEY);
        if (savedState) {
          try {
            state = JSON.parse(savedState);
          } catch {
            // Invalid state, ignore
          }
        }
      }

      return {
        pathname: routeData.pathname,
        state: routeData.pathname === '/chat' ? undefined : state,
      };
    } catch (error) {
      console.error('Error getting saved route:', error);
      return null;
    }
  }

  /**
   * Clear saved route
   */
  static clearRoute(): void {
    try {
      sessionStorage.removeItem(this.ROUTE_KEY);
      sessionStorage.removeItem(this.ROUTE_STATE_KEY);
    } catch (error) {
      console.error('Error clearing route:', error);
    }
  }

  /**
   * Check if we should restore route (e.g., after app was backgrounded)
   */
  static shouldRestoreRoute(): boolean {
    try {
      const saved = sessionStorage.getItem(this.ROUTE_KEY);
      if (!saved) {
        return false;
      }

      const routeData = JSON.parse(saved);
      const now = Date.now();
      
      // Only restore if route was saved recently (within last hour)
      return (now - routeData.timestamp) < 60 * 60 * 1000;
    } catch {
      return false;
    }
  }
}

export default RoutePersistenceService;



